<template>
  <LoadingComponent v-if="loading" />
  <template v-if="!loading && localDashboard">
    <div class="shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <DashboardPanelControl
            ref="panel_control"
            v-model:search-value="searchValue"
            v-model:dashboard="localDashboard"
            v-model:require-save-dashboard="requireSaveDashboard"
            :count-filter="validFilter.length"
            :is-dirty="$isStateDirty('localDashboard.filters')"
            :need-save-locked="loading"
            @dashboard-changed="dashboardChanged"
            @export-file="handleExportFile"
            @clear-filters="clearFilters"
          >
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                :dashboard-type="localDashboard.type"
                @close-popover="() => closePopover(close)"
              />
            </template>
            <template v-if="false" #display-options-content>
              <DisplayOptionsContent
                v-model:display-options="localDashboard.display_options"
              />
            </template>
            <template #other-cta>
              <AddNewColumnComponent model="payment" @add-column="addColumn" />
            </template>
          </DashboardPanelControl>
        </template>
        <template #subtitle>
          <div class="flex items-center gap-4">
            <template v-if="hasFilterDates">
              <div class="shrink-0">
                <DateRangeNavigator
                  v-model:start="specific_day_start"
                  v-model:end="specific_day_end"
                  size="small"
                  @dates-changed="rangeNavigatorChanged"
                />
              </div>
              <div class="text-lg font-bold capitalize">
                {{ periodTitle }}
              </div>
            </template>
          </div>
        </template>
      </DashboardHeader>
    </div>
    <div class="relative flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <div
        v-if="loadingPayments"
        class="absolute top-1/2 left-1/2 z-100 h-36 w-36 -translate-x-1/2 -translate-y-1/2 transform rounded-2xl bg-gray-200"
      >
        <LoadingComponent :display-label="false" />
      </div>
      <PaymentTable
        v-model:columns="localDashboard.columns"
        v-model:sort="localDashboard.sort"
        v-model:require-save-dashboard="requireSaveDashboard"
        :payments="filtered_payments"
        @fetch-payments="fetchPayments"
        @update:dashboard="updateDashboard"
      />
    </div>
  </template>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import DashboardPanelControl from '../../Components/DashboardPanelControl.vue'
import DisplayOptionsContent from '@c/Dashboard/Content/Invoicing/Panel/DisplayOptionsContent.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import AddNewColumnComponent from '@c/Dashboard/Content/Invoicing/components/Head/columnSettings/AddNewColumnComponent.vue'
import DateRangeNavigator from '@c/SelectComponent/DateRangeNavigator.vue'
import PaymentTable from '@c/Dashboard/Content/Payment/PaymentTable.vue'
import {
  rowMatchesSearch,
  searchableKeys
} from '@c/Dashboard/Components/dashboardSearch'
import persistViewFiltersStateMixin from '@/mixins/persistViewFiltersStateMixin'

export default {
  name: 'PaymentContent',
  components: {
    PaymentTable,
    DateRangeNavigator,
    AddNewColumnComponent,
    DisplayOptionsContent,
    FilterLinesComponent,
    DashboardPanelControl,
    DashboardHeader,
    LoadingComponent
  },
  mixins: [persistViewFiltersStateMixin],
  persistViewState: [
    'searchValue',
    'specific_day_enabled',
    'specific_day_start',
    'specific_day_end',
    'localDashboard.filters'
  ],
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingPayments: false,
      searchValue: '',
      specific_day_enabled: false,
      specific_day_start: null,
      specific_day_end: null,
      paymentList: [],
      localDashboard: null,
      requireSaveDashboard: false
    }
  },
  computed: {
    ...mapGetters({
      metadata: 'metadata/getInitialMetadata',
      availableFilter: 'metadata/getAvailableFilterPayment'
    }),
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    },
    filtrableColumns() {
      return searchableKeys(this.localDashboard.columns)
    },
    filtered_payments() {
      let tmpPaymentList = [...this.paymentList]
      if (this.searchValue.length >= 2) {
        const searchValue = this.$h.normalizeSearch(this.searchValue)
        tmpPaymentList = tmpPaymentList.filter((model) =>
          rowMatchesSearch(model, this.filtrableColumns, searchValue, (value) =>
            this.$h.normalizeSearch(value)
          )
        )
      }
      if (this.localDashboard.sort?.column_id) {
        let sortRealColumn = this.localDashboard.columns.find(
          (column) => column.id === this.localDashboard.sort.column_id
        )
        let sortRealColumnValue =
          sortRealColumn?.sort?.value ?? sortRealColumn?.field

        if (sortRealColumnValue) {
          tmpPaymentList.sort((a, b) => {
            if (sortRealColumn.type === 'currency') {
              let aValue = parseFloat(a[sortRealColumnValue] ?? 0)
              let bValue = parseFloat(b[sortRealColumnValue] ?? 0)
              return aValue - bValue
            } else {
              let aValue = a[sortRealColumnValue] ?? ''
              let bValue = b[sortRealColumnValue] ?? ''
              return aValue.localeCompare(bValue)
            }
          })

          if (this.localDashboard.sort.direction === 'desc') {
            tmpPaymentList.reverse()
          }
        }
      }
      return tmpPaymentList
    },
    hasFilterDates() {
      return this.localDashboard.filters
        ?.map((filter) => filter.column)
        ?.some((column) =>
          [
            'creation_date_specific',
            'date_relative',
            'creation_date_range'
          ].includes(column)
        )
    },
    periodTitle() {
      if (
        this.specific_day_enabled &&
        this.specific_day_start &&
        this.specific_day_end
      ) {
        return this.$h.getDateRangeString(
          this.specific_day_start,
          this.specific_day_end,
          'ddd ll'
        )
      }

      const filters =
        this.localDashboard.filters?.filter((filter) =>
          [
            'creation_date_range',
            'creation_date_specific',
            'date_relative'
          ].includes(filter.column)
        ) ?? []
      console.log(filters)

      if (filters.length && filters[0].values) {
        const { column, values } = filters[0]

        console.log(column, values)
        if (column === 'creation_date_range' && values?.start && values?.end) {
          return this.$h.getDateRangeString(values.start, values.end, 'ddd ll')
        } else if (column === 'date_relative' && values) {
          try {
            if (typeof values === 'string' && values.trim() !== '') {
              return `${this.$t(
                `enums.common.relativeDates.${values}`
              )} - ${this.$h.getRelativeDateString(values, 'ddd ll')}`
            } else {
              return ''
            }
          } catch (error) {
            return ''
          }
        } else if (column === 'creation_date_specific' && values) {
          return this.$h.getDateRangeString(values, values, 'ddd ll')
        }
      }

      return '-'
    }
  },
  watch: {
    currentBoard(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.localDashboard = newValue
        this.fetchTemplateDashboardConfig()
      }
    },
    '$route.meta.doc_type'() {
      this.paymentList = []
      this.fetchPayments()
      if (this.requireSaveDashboard) {
        this.requireSaveDashboard = false
      }
    },
    'localDashboard.filters': {
      handler(newValue) {
        this.filterUpdated()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchTemplateDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchListPaymentAction: 'dashboard/fetchListPaymentDashboard',
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processPaymentExportExcelAction: 'invoicing/paymentExportExcel'
    }),
    clearFilters() {
      this.localDashboard.filters = this.$getInitialState(
        'localDashboard.filters'
      )
      this.fetchPayments()
    },
    fetchTemplateDashboardConfig() {
      this.loading = true
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = response.template_dashboard
          this.$restoreCachedState(
            'localDashboard.filters',
            response.template_dashboard.filters
          )
          this.fetchPayments()
        })
        .catch((error) => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
        .finally((_) => {
          if (this.currentBoard.id === targetId) this.loading = false
        })
    },
    updateDashboard(reloadDossier) {
      this.$refs.panel_control.saveDashboard(() => {
        if (reloadDossier) {
          this.fetchPayments()
        }
      })
    },
    fetchPayments() {
      this.paymentList = []
      this.loadingPayments = true
      this.processFetchListPaymentAction({
        filters: this.localDashboard.filters,
        columns: this.localDashboard.columns,
        template_id: this.localDashboard.id
      })
        .then((response) => {
          this.paymentList = response.data.payments
          this.filterUpdated()
        })
        .catch(() => {})
        .finally(() => {
          this.loadingPayments = false
        })
    },
    fetchPaymentsFromShortDates() {
      if (
        this.specific_day_enabled &&
        this.specific_day_start &&
        this.specific_day_end
      ) {
        const values = {
          start: this.specific_day_start,
          end: this.specific_day_end
        }
        console.log(values)
        const filters = (this.localDashboard.filters || []).map((filter) => {
          if (
            [
              'creation_date_specific',
              'creation_date_range',
              'date_relative'
            ].includes(filter.column)
          ) {
            return { ...filter, column: 'creation_date_range', values: values }
          }
          return filter
        })
        console.log(filters)

        this.loadingPayments = true
        this.processFetchListPaymentAction({
          filters: filters,
          columns: this.localDashboard.columns,
          template_id: this.localDashboard.id
        })
          .then((response) => {
            this.paymentList = response.data.payments
          })
          .catch(() => {})
          .finally(() => {
            this.loadingPayments = false
          })
      }
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchPayments()
    },
    dashboardChanged(dashboard_template_id) {},
    addColumn(config) {
      this.localDashboard.columns.push({
        ...config,
        sum_able: false,
        template_id: this.localDashboard.id
      })
    },
    filterUpdated() {
      console.log(this.localDashboard.filters)
      let filters =
        this.localDashboard.filters.filter((filter) =>
          [
            'creation_date_range',
            'creation_date_specific',
            'date_relative'
          ].includes(filter.column)
        ) ?? []
      console.log(filters)
      if (filters.length && filters[0].values !== null) {
        if (filters[0].column === 'creation_date_range') {
          this.specific_day_start = filters[0].values.start
          this.specific_day_end = filters[0].values.end
        } else if (filters[0].column === 'date_relative') {
          let relativeDates = this.$h.getRelativeDateAsArray(filters[0].values)
          if (Array.isArray(relativeDates) && relativeDates.length === 2) {
            this.specific_day_start = relativeDates[0].format('YYYY-MM-DD')
            this.specific_day_end = relativeDates[1].format('YYYY-MM-DD')
          }
        } else if (filters[0].column === 'creation_date_specific') {
          this.specific_day_start = filters[0].values
          this.specific_day_end = filters[0].values
        }
      }
    },
    rangeNavigatorChanged() {
      this.specific_day_enabled = true
      this.fetchPaymentsFromShortDates()
    },
    handleExportFile() {
      this.processPaymentExportExcelAction({
        filters: this.localDashboard.filters,
        columns: this.localDashboard.columns,
        template_id: this.localDashboard.id,
        sort: this.localDashboard.sort,
        terms: this.searchValue
      }).then((response) => {
        window.open(response.url)
      })
    }
  }
}
</script>

<style scoped></style>
